/**
 * Canonical BrandStore market defaults.
 *
 * Country and territory remain the economic authority. Currency is deliberately
 * not stored here: it continues to resolve from `territories.currency`.
 * Locale and time-zone defaults are presentation settings that can later be
 * overridden per BrandStore without changing economic routing.
 */

export interface BrandStoreMarketDefaults {
  defaultLocale: string;
  supportedLocales: readonly string[];
  timeZone: string;
}

const FALLBACK_MARKET_DEFAULTS: BrandStoreMarketDefaults = Object.freeze({
  defaultLocale: 'en-US',
  supportedLocales: Object.freeze(['en-US']),
  timeZone: 'UTC',
});

const COUNTRY_MARKET_DEFAULTS: Readonly<Record<string, BrandStoreMarketDefaults>> = Object.freeze({
  PL: Object.freeze({
    defaultLocale: 'pl-PL',
    supportedLocales: Object.freeze(['pl-PL', 'en-US']),
    timeZone: 'Europe/Warsaw',
  }),
});

/** Normalize an ISO 3166-1 alpha-2 country code for registry lookup. */
export function normalizeMarketCountryCode(countryCode: string | null | undefined): string | null {
  const normalized = countryCode?.trim().toUpperCase() ?? '';
  return /^[A-Z]{2}$/.test(normalized) ? normalized : null;
}

/**
 * Resolve presentation defaults for a BrandStore country.
 *
 * Poland is the first complete adapter. Countries without a dedicated adapter
 * receive the rollout-safe English/UTC fallback until their adapter is added.
 */
export function resolveBrandStoreMarketDefaults(
  countryCode: string | null | undefined,
): BrandStoreMarketDefaults {
  const normalized = normalizeMarketCountryCode(countryCode);
  const defaults = (normalized && COUNTRY_MARKET_DEFAULTS[normalized]) || FALLBACK_MARKET_DEFAULTS;

  return {
    defaultLocale: defaults.defaultLocale,
    supportedLocales: [...defaults.supportedLocales],
    timeZone: defaults.timeZone,
  };
}

/** Minimal BCP 47 shape used by the database migration and client validation. */
export function isBrandStoreLocaleTag(value: string | null | undefined): boolean {
  return /^[A-Za-z]{2,3}(?:-[A-Za-z0-9]{2,8})*$/.test(value?.trim() ?? '');
}
